import { AuthUserTypeName, UserAuthRole } from '@/domain/entities/auth';
import { decodeJwtPayload, type CognitoIdTokenPayload } from '@/lib/utils';

export interface CognitoAuthorizationClaims {
  userId: string | null;
  authRole: UserAuthRole | null;
  authUserType: AuthUserTypeName | null;
}

function parseAuthRole(value: unknown): UserAuthRole | null {
  return Object.values(UserAuthRole).includes(value as UserAuthRole) ? (value as UserAuthRole) : null;
}

function parseAuthUserType(value: unknown): AuthUserTypeName | null {
  return Object.values(AuthUserTypeName).includes(value as AuthUserTypeName) ? (value as AuthUserTypeName) : null;
}

/**
 * Extracts Cognito claims used only for client-side UI and routing.
 * Privileged API operations remain authorized by the backend.
 */
export function parseCognitoIdToken(idToken: string): CognitoAuthorizationClaims {
  const payload = decodeJwtPayload<CognitoIdTokenPayload>(idToken);

  return {
    userId: typeof payload?.sub === 'string' && payload.sub.trim() ? payload.sub : null,
    authRole: parseAuthRole(payload?.['custom:role']),
    authUserType: parseAuthUserType(payload?.['custom:userType']),
  };
}

export function isCognitoSystemSuperAdmin(
  authRole: UserAuthRole | null | undefined,
  authUserType: AuthUserTypeName | null | undefined,
): boolean {
  return authRole === UserAuthRole.SuperAdmin && authUserType === AuthUserTypeName.SystemUser;
}
